import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAccessToken } from "@/lib/auth";
import type { LogRecord } from "./types";

const DEFAULT_BUFFER_CAP = 10_000;
const BACKOFF_STEPS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

export type StreamStatus =
  | "idle"
  | "connecting"
  | "open"
  | "reconnecting"
  | "closed"
  | "error"
  | "missing-pod";

export interface UseLogsStreamOptions {
  /**
   * App name — used to hit `/api/cluster/apps/:name/logs/stream`.
   * When not provided, the stream stays idle (useful for rendering the
   * viewer before a pod has been selected).
   */
  appName?: string;
  /** Pod to follow. If omitted, backend picks the first pod for the app. */
  pod?: string;
  /** Container to follow. Backend passes this to k8s log options. */
  container?: string;
  /** Override the default 200-line initial tail. */
  tailLines?: number;
  /** Fetch previous-container logs (crash inspection). */
  previous?: boolean;
  /** Ring buffer max size. Older records are dropped when exceeded. */
  bufferCap?: number;
  /** When false, disables auto-connect. Useful when mode === 'history'. */
  enabled?: boolean;
}

export interface UseLogsStreamResult {
  records: LogRecord[];
  status: StreamStatus;
  error: string | null;
  /** True iff a reconnect attempt is pending. */
  reconnecting: boolean;
  /** Monotonic reconnect counter; useful for UI banners. */
  reconnectAttempt: number;
  /** Manually flush the buffered records. */
  clear: () => void;
  /** Force-reconnect now (e.g. after changing pod selection). */
  reconnect: () => void;
}

interface SseEvent {
  event: string;
  data: string;
}

/**
 * Parse a chunk of SSE wire data. Handles the standard `event: ...\ndata: ...`
 * framing. Returns parsed events plus any leftover partial frame so the
 * caller can re-feed it on the next chunk.
 */
function parseSseChunk(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  let idx: number;
  while ((idx = buffer.indexOf("\n\n")) !== -1) {
    const frame = buffer.slice(0, idx);
    buffer = buffer.slice(idx + 2);

    let eventName = "message";
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) eventName = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
    }
    if (dataLines.length > 0) {
      events.push({ event: eventName, data: dataLines.join("\n") });
    }
  }
  return { events, rest: buffer };
}

/**
 * Subscribes to the SSE log stream endpoint, buffering records in a ring
 * and auto-reconnecting with exponential backoff on failure.
 *
 * Implementation note: uses `fetch` + `ReadableStream` (not EventSource) so
 * the Keycloak bearer token can go in the `Authorization` header rather than
 * the query string — avoids leaking tokens into access logs. The SSE wire
 * format is parsed manually in `parseSseChunk`.
 */
export function useLogsStream(opts: UseLogsStreamOptions): UseLogsStreamResult {
  const {
    appName,
    pod,
    container,
    tailLines = 200,
    previous = false,
    bufferCap = DEFAULT_BUFFER_CAP,
    enabled = true,
  } = opts;

  const [records, setRecords] = useState<LogRecord[]>([]);
  const [status, setStatus] = useState<StreamStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const attemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);
  const mountedRef = useRef(true);

  const url = useMemo(() => {
    if (!appName) return null;
    const params = new URLSearchParams();
    if (pod) params.set("pod", pod);
    if (container) params.set("container", container);
    if (tailLines) params.set("tailLines", String(tailLines));
    if (previous) params.set("previous", "true");
    return `/api/cluster/apps/${encodeURIComponent(appName)}/logs/stream?${params.toString()}`;
  }, [appName, pod, container, tailLines, previous]);

  const clear = useCallback(() => setRecords([]), []);

  const closeStream = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (!mountedRef.current) return;
    const stepIdx = Math.min(attemptRef.current, BACKOFF_STEPS_MS.length - 1);
    const delay = BACKOFF_STEPS_MS[stepIdx]!;
    attemptRef.current += 1;
    setReconnectAttempt(attemptRef.current);
    setStatus("reconnecting");
    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current) void connectRef.current?.();
    }, delay);
  }, []);

  // Stable ref to the current connect function so scheduleReconnect doesn't
  // capture a stale closure.
  const connectRef = useRef<(() => Promise<void>) | null>(null);

  const connect = useCallback(async () => {
    if (!enabled || !url) {
      closeStream();
      setStatus("idle");
      return;
    }

    closeStream();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus(attemptRef.current === 0 ? "connecting" : "reconnecting");

    let token: string | null = null;
    try {
      token = await getAccessToken();
    } catch {
      // fall through — backend will 401 and we'll surface the error
    }
    if (!mountedRef.current || controller.signal.aborted) return;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        if (response.status === 404) {
          setStatus("missing-pod");
          setError("Под не найден");
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      if (!response.body) {
        throw new Error("Пустой ответ");
      }

      attemptRef.current = 0;
      setReconnectAttempt(0);
      setError(null);
      setStatus("open");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!controller.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseChunk(buffer);
        buffer = rest;

        if (events.length > 0 && mountedRef.current) {
          const newRecords: LogRecord[] = [];
          for (const evt of events) {
            if (evt.event === "log") {
              try {
                const data = JSON.parse(evt.data) as {
                  ts: string;
                  level: string;
                  msg: string;
                  pod: string;
                  container: string | null;
                };
                newRecords.push({
                  id: `${data.ts}-${seqRef.current++}`,
                  ts: data.ts,
                  level: data.level,
                  msg: data.msg,
                  pod: data.pod,
                  container: data.container,
                });
              } catch {
                // drop malformed line
              }
            } else if (evt.event === "error") {
              try {
                const data = JSON.parse(evt.data) as { message?: string };
                setError(data.message ?? "Ошибка потока");
              } catch {
                // ignore
              }
            }
            // `open` and `ping` events are acknowledged implicitly
          }

          if (newRecords.length > 0) {
            setRecords((prev) => {
              const combined =
                prev.length + newRecords.length > bufferCap
                  ? [...prev, ...newRecords].slice(-bufferCap)
                  : [...prev, ...newRecords];
              return combined;
            });
          }
        }
      }

      if (mountedRef.current && !controller.signal.aborted) {
        // Server closed cleanly — reconnect with backoff.
        scheduleReconnect();
      }
    } catch (err) {
      if (controller.signal.aborted || !mountedRef.current) return;
      setError(err instanceof Error ? err.message : "Не удалось подключиться");
      scheduleReconnect();
    }
  }, [enabled, url, closeStream, bufferCap, scheduleReconnect]);

  // Keep the ref in sync for scheduleReconnect's indirect call.
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const reconnect = useCallback(() => {
    attemptRef.current = 0;
    setReconnectAttempt(0);
    void connect();
  }, [connect]);

  // Connect when url or enabled changes. Clearing on pod/container change
  // avoids showing stale rows from the previous pod.
  useEffect(() => {
    mountedRef.current = true;
    setRecords([]);
    seqRef.current = 0;
    attemptRef.current = 0;
    setReconnectAttempt(0);
    setError(null);
    void connect();
    return () => {
      mountedRef.current = false;
      closeStream();
    };
  }, [connect, closeStream]);

  return {
    records,
    status,
    error,
    reconnecting: status === "reconnecting",
    reconnectAttempt,
    clear,
    reconnect,
  };
}
